/**
 * IndexedDB Storage Utility
 * Handles device key and encrypted vault storage in IndexedDB
 */

const DB_NAME = 'wallet_vault_db';
const DB_VERSION = 1;
const DEVICE_KEY_STORE = 'device_keys';
const VAULT_STORE = 'vaults';

/**
 * Open IndexedDB database
 */
function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create device keys store
      if (!db.objectStoreNames.contains(DEVICE_KEY_STORE)) {
        const deviceKeyStore = db.createObjectStore(DEVICE_KEY_STORE, { keyPath: 'id' });
        deviceKeyStore.createIndex('userId', 'userId', { unique: false });
      }

      // Create vault store
      if (!db.objectStoreNames.contains(VAULT_STORE)) {
        const vaultStore = db.createObjectStore(VAULT_STORE, { keyPath: 'userId' });
      }
    };
  });
}

/**
 * Store device key for a user
 * @param {string} userId - Supabase user ID
 * @param {string} deviceKey - Encrypted device key (base64)
 * @param {string} deviceId - Unique device identifier
 */
export async function storeDeviceKey(userId, deviceKey, deviceId) {
  try {
    const db = await openDB();
    const transaction = db.transaction([DEVICE_KEY_STORE], 'readwrite');
    const store = transaction.objectStore(DEVICE_KEY_STORE);

    await new Promise((resolve, reject) => {
      const request = store.put({
        id: `${userId}_${deviceId}`,
        userId,
        deviceId,
        deviceKey,
        createdAt: new Date().toISOString(),
      });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
  } catch (error) {
    console.error('Error storing device key:', error);
    throw error;
  }
}

/**
 * Get device key for a user
 * @param {string} userId - Supabase user ID
 * @param {string} deviceId - Unique device identifier
 * @returns {Promise<string|null>} - Device key (base64) or null if not found
 */
export async function getDeviceKey(userId, deviceId) {
  try {
    const db = await openDB();
    const transaction = db.transaction([DEVICE_KEY_STORE], 'readonly');
    const store = transaction.objectStore(DEVICE_KEY_STORE);

    const key = await new Promise((resolve, reject) => {
      const request = store.get(`${userId}_${deviceId}`);

      request.onsuccess = () => resolve(request.result?.deviceKey || null);
      request.onerror = () => reject(request.error);
    });

    db.close();
    return key;
  } catch (error) {
    console.error('Error getting device key:', error);
    throw error;
  }
}

/**
 * Store encrypted vault for a user
 * Stores in both IndexedDB (for performance) and Supabase (for cross-device recovery)
 * @param {string} userId - Supabase user ID
 * @param {string} encryptedVault - Encrypted vault data (base64)
 * @param {boolean} syncToSupabase - Whether to sync to Supabase (default: true)
 */
export async function storeVault(userId, encryptedVault, syncToSupabase = true) {
  try {
    // Store in IndexedDB (fast, local access)
    const db = await openDB();
    const transaction = db.transaction([VAULT_STORE], 'readwrite');
    const store = transaction.objectStore(VAULT_STORE);

    await new Promise((resolve, reject) => {
      const request = store.put({
        userId,
        encryptedVault,
        updatedAt: new Date().toISOString(),
      });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();

    // Also store in Supabase user metadata (for cross-device recovery)
    if (syncToSupabase) {
      try {
        const { supabase } = await import('../services/supabase-client');
        const { error } = await supabase.auth.updateUser({
          data: { encrypted_vault: encryptedVault }
        });
        if (error) {
          console.warn('Failed to sync vault to Supabase (non-critical):', error);
          // Don't throw - IndexedDB storage succeeded, Supabase sync is best-effort
        }
      } catch (supabaseError) {
        console.warn('Error syncing vault to Supabase (non-critical):', supabaseError);
        // Don't throw - IndexedDB storage succeeded
      }
    }
  } catch (error) {
    console.error('Error storing vault:', error);
    throw error;
  }
}

/**
 * Get encrypted vault for a user
 * Checks IndexedDB first (fast), then Supabase (for cross-device recovery)
 * @param {string} userId - Supabase user ID
 * @param {boolean} checkSupabase - Whether to check Supabase if not in IndexedDB (default: true)
 * @returns {Promise<string|null>} - Encrypted vault (base64) or null if not found
 */
export async function getVault(userId, checkSupabase = true) {
  try {
    // Try IndexedDB first (fast, local access)
    const db = await openDB();
    const transaction = db.transaction([VAULT_STORE], 'readonly');
    const store = transaction.objectStore(VAULT_STORE);

    const vault = await new Promise((resolve, reject) => {
      const request = store.get(userId);

      request.onsuccess = () => resolve(request.result?.encryptedVault || null);
      request.onerror = () => reject(request.error);
    });

    db.close();

    // If found in IndexedDB, return it
    if (vault) {
      return vault;
    }

    // If not in IndexedDB and checkSupabase is true, try Supabase (for new devices)
    if (checkSupabase) {
      try {
        const { supabase } = await import('../services/supabase-client');
        const { data: { user }, error } = await supabase.auth.getUser();
        
        if (error) {
          console.warn('Error getting user from Supabase:', error);
          return null;
        }

        if (user?.user_metadata?.encrypted_vault) {
          const supabaseVault = user.user_metadata.encrypted_vault;
          
          // Store in IndexedDB for future fast access
          try {
            await storeVault(userId, supabaseVault, false); // Don't sync back to Supabase (we just got it from there)
          } catch (syncError) {
            console.warn('Failed to sync vault from Supabase to IndexedDB:', syncError);
            // Continue anyway - we have the vault from Supabase
          }
          
          return supabaseVault;
        }
      } catch (supabaseError) {
        console.warn('Error getting vault from Supabase:', supabaseError);
        // Return null - vault not found in either location
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting vault:', error);
    throw error;
  }
}

/**
 * Remove device key for a user
 * @param {string} userId - Supabase user ID
 * @param {string} deviceId - Unique device identifier
 */
export async function removeDeviceKey(userId, deviceId) {
  try {
    const db = await openDB();
    const transaction = db.transaction([DEVICE_KEY_STORE], 'readwrite');
    const store = transaction.objectStore(DEVICE_KEY_STORE);

    await new Promise((resolve, reject) => {
      const request = store.delete(`${userId}_${deviceId}`);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
  } catch (error) {
    console.error('Error removing device key:', error);
    throw error;
  }
}

/**
 * Remove vault for a user
 * @param {string} userId - Supabase user ID
 */
export async function removeVault(userId) {
  try {
    const db = await openDB();
    const transaction = db.transaction([VAULT_STORE], 'readwrite');
    const store = transaction.objectStore(VAULT_STORE);

    await new Promise((resolve, reject) => {
      const request = store.delete(userId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
  } catch (error) {
    console.error('Error removing vault:', error);
    throw error;
  }
}

/**
 * Generate a unique device ID
 * @returns {string} - Device ID
 */
export function generateDeviceId() {
  // Use a combination of browser fingerprint and random string
  const stored = localStorage.getItem('device_id');
  if (stored) return stored;

  const deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem('device_id', deviceId);
  return deviceId;
}

/**
 * Check if device key exists for user
 * @param {string} userId - Supabase user ID
 * @param {string} deviceId - Unique device identifier
 * @returns {Promise<boolean>}
 */
export async function hasDeviceKey(userId, deviceId) {
  const key = await getDeviceKey(userId, deviceId);
  return key !== null;
}
